import { Calendar, MenuIcon, Plus, Table, ThumbsDownIcon, ThumbsUpIcon, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import type { FC, ReactNode } from "react";
import { LogoIcon } from "../header/Header";
import TableManagementTab from "./TableManagementTab";
import ReservationDetail from "./ReservationDetail";
import { BookingsIcon, DashboardIcon, LogoutIcon, RestaurantIcon, ChevronDownIcon, PeopleIcon, CalendarXIcon, ChevronLeftIcon, ChevronRightIcon, PrintIcon,InfoIcon, SlashCircleIcon, HelpCenterIcon, TableIcon } from "./Icons"
import BookingsTab from "./BookingsTab";
import HelpCenter from "./HelpCenterTab";
import { useAuth } from "../Context/AuthContext";
import DashboardTab from "./DashboardTab";
import type { Restaurant } from "../Body/Manager/restaurant.types";
import { createReservation, getSecureRestaurant } from "../lib/api/reservations.apis";
import { useNavigate, useParams } from "react-router-dom";
import FloorPlanBuilder from "./FloorMap";
import OnlineReservationsToggle from "./Components/OnlineReservationsToggle";

// ─── TYPES ────────────────────────────────────────────────────────────────────

type ReservationStatus = "confirmed" | "pending" | "seated" | "completed" | "cancelled" | "no_show" | "waitlist";
type ActiveTab = "dashboard" | "bookings" | "tables" | "reservation-details" | "floor-map" | "help-center";
const STATUS_CONFIG: Record<ReservationStatus, { dot: string; text: string; bg: string; label: string }> = {
  confirmed: { dot: "bg-green-500",  text: "text-green-600",  bg: "bg-green-50",  label: "Confirmed" },
  pending:   { dot: "bg-amber-400",  text: "text-amber-600",  bg: "bg-amber-50",  label: "Pending"   },
  seated:    { dot: "bg-blue-500",   text: "text-blue-600",   bg: "bg-blue-50",   label: "Seated"    },
  completed: { dot: "bg-violet-500", text: "text-violet-600", bg: "bg-violet-50", label: "Completed" },
  cancelled: { dot: "bg-red-500",    text: "text-red-600",    bg: "bg-red-50",    label: "Cancelled" },
  no_show:   { dot: "bg-gray-400",   text: "text-gray-500",   bg: "bg-gray-100",  label: "No Show"   },
  waitlist:  { dot: "bg-orange-400", text: "text-orange-600", bg: "bg-orange-50", label: "Waitlist"  },
};

function generateTimes(startHour = 9, endHour = 22): string[] {
  const times: string[] = [];
  for (let h = startHour; h <= endHour; h++) {
    times.push(`${String(h).padStart(2, "0")}:00`);
    if (h < endHour) times.push(`${String(h).padStart(2, "0")}:30`);
  }
  return times;
}

function generateDurations(): { value: number; label: string }[] {
  const durations = [];
  for (let mins = 15; mins <= 480; mins += 15) {
    const h = Math.floor(mins / 60), m = mins % 60;
    const label = h > 0 ? (m > 0 ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
    durations.push({ value: mins, label });
  }
  return durations;
}

// ─── MINI CALENDAR ────────────────────────────────────────────────────────────
const MiniCalendar: FC<{ value: Date | null; onChange: (d: Date) => void }> = ({ value, onChange }) => {
  const today = new Date();
  const [viewDate, setViewDate] = useState(value || today);
  const year = viewDate.getFullYear(), month = viewDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();
  const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const days: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  const isSelected = (d: number | null) =>
    !!(value && d && value.getFullYear() === year && value.getMonth() === month && value.getDate() === d);
  const isToday = (d: number | null) =>
    !!(d && today.getFullYear() === year && today.getMonth() === month && today.getDate() === d);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-3 w-64 shadow-2xl z-50">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setViewDate(new Date(year, month - 1, 1))} className="text-gray-400 hover:text-gray-700 text-xl px-1 bg-transparent border-none cursor-pointer leading-none">‹</button>
        <span className="text-sm font-semibold text-stone-800">{monthNames[month]} {year}</span>
        <button onClick={() => setViewDate(new Date(year, month + 1, 1))} className="text-gray-400 hover:text-gray-700 text-xl px-1 bg-transparent border-none cursor-pointer leading-none">›</button>
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
          <div key={d} className="text-center text-[10px] font-bold text-gray-400 py-0.5">{d}</div>
        ))}
        {days.map((d, i) => (
          <button key={i} onClick={() => d && onChange(new Date(year, month, d))}
            className={`rounded-lg py-1.5 text-xs border-none cursor-pointer transition-all
              ${!d ? "invisible" : ""}
              ${isSelected(d) ? "bg-orange-500 text-white font-bold" : isToday(d) ? "bg-amber-50 text-orange-500 font-semibold" : "bg-transparent text-gray-700 hover:bg-gray-100"}`}>
            {d || ""}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── SHARED COMPONENTS ────────────────────────────────────────────────────────
const FieldLabel: FC<{ children: ReactNode }> = ({ children }) => (
  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 m-0">{children}</p>
);

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-white outline-none focus:border-orange-400 transition-colors placeholder-gray-300 box-border";
const selectCls = `${inputCls}`;

// ─── BOOKING MODAL ────────────────────────────────────────────────────────────
const BookingModal: FC<{
  onClose: () => void;
  restaurantName?: string;
  restaurantId: string;
  accessToken?: string | null;
}> = ({ onClose, restaurantName, restaurantId, accessToken }) => {
  const [step, setStep] = useState(1);
  const [showCal, setShowCal] = useState(false);
  const [bookingDate, setBookingDate] = useState<Date>(new Date());
  const [bookingTime, setBookingTime] = useState("19:00");
  const [duration, setDuration] = useState(120);
  const [partySize, setPartySize] = useState(2);
  const [otherParty, setOtherParty] = useState(false);
  const [otherPartyVal, setOtherPartyVal] = useState("");
  const [channel, setChannel] = useState("walk_in");
  const [status, setStatus] = useState("confirmed");
  const [privateNote, setPrivateNote] = useState("");
  const [bookingComment, setBookingComment] = useState("");
  const [patron, setPatron] = useState({ name: "", phone: "", email: "", tags: [] as string[], vip: false });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const TAG_OPTIONS = ["Vegetarian", "Vegan", "Gluten Intolerant", "Lactose Intolerant"];
  const toggleTag = (t: string) => setPatron(p => ({ ...p, tags: p.tags.includes(t) ? p.tags.filter(x => x !== t) : [...p.tags, t] }));
  const dateStr = bookingDate.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });

  const handleCreateBooking = async () => {
    if (!restaurantId) {
      setSubmitError("Missing restaurant ID.");
      return;
    }
    if (!patron.name.trim() || !patron.phone.trim()) {
      setSubmitError("Guest name and phone are required.");
      return;
    }

    const [hours, minutes] = bookingTime.split(":").map(Number);
    const start = new Date(bookingDate);
    start.setHours(hours, minutes, 0, 0);
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + duration);

    try {
      setSubmitting(true);
      setSubmitError(null);
      await createReservation(
        restaurantId,
        {
          guestName: patron.name.trim(),
          phone: patron.phone.trim(),
          email: patron.email.trim() || undefined,
          partySize,
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          specialRequest: bookingComment.trim() || undefined,
          notesInternal: privateNote.trim() || undefined,
          status: status as ReservationStatus,
          source: channel,
        },
        { token: accessToken }
      );
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to create booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-stone-900/30 z-[1000] flex items-center justify-center p-2 md:p-4">
      <div className="bg-white rounded-md w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">

        {/* Modal Header */}
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-2 md:px-7 py-4 border-b border-gray-100 rounded-t-2xl">
          <div>
            <h2 className="m-0 text-xl font-bold text-stone-900">New Booking</h2>
            <p className="m-0 mt-0.5 text-xs text-gray-400">{restaurantName || "Restaurant"}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2].map(s => (
                <button key={s} onClick={() => setStep(s)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border-none cursor-pointer transition-all
                    ${step === s ? "bg-orange-500 text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                  Step {s}
                </button>
              ))}
            </div>
            <button onClick={onClose} className="bg-gray-100 hover:bg-gray-200 border-none cursor-pointer rounded-xl p-2 flex items-center text-gray-500 transition-colors">
              <X />
            </button>
          </div>
        </div>

        <div className="px-3 md:px-7 py-3 md:py-6">
          {step === 1 ? (
            <div className="flex flex-col gap-5">
              {/* Date */}
              <div className="relative">
                <FieldLabel>Date</FieldLabel>
                <button onClick={() => setShowCal(!showCal)}
                  className="w-full flex items-center justify-between px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-stone-800 bg-gray-50 cursor-pointer hover:border-orange-300 transition-colors">
                  <span>{dateStr}</span><ChevronDownIcon />
                </button>
                {showCal && (
                  <div className="absolute top-full left-0 mt-2 z-50">
                    <MiniCalendar value={bookingDate} onChange={d => { setBookingDate(d); setShowCal(false); }} />
                  </div>
                )}
              </div>

              {/* Time & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Time</FieldLabel>
                  <select value={bookingTime} onChange={e => setBookingTime(e.target.value)} className={selectCls}>
                    {generateTimes().map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <FieldLabel>Duration</FieldLabel>
                  <select value={duration} onChange={e => setDuration(Number(e.target.value))} className={selectCls}>
                    {generateDurations().map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Party Size */}
              <div>
                <FieldLabel>Party Size</FieldLabel>
                <div className="grid grid-cols-5 gap-2 mb-2">
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => { setPartySize(n); setOtherParty(false); }}
                      className={`py-2.5 rounded-xl border-2 font-bold text-base cursor-pointer transition-all
                        ${partySize === n && !otherParty
                          ? "border-orange-500 bg-orange-50 text-orange-500"
                          : "border-gray-200 bg-gray-50 text-stone-700 hover:border-orange-300"}`}>
                      {n}
                    </button>
                  ))}
                </div>
                  <input type="number" className={`${inputCls} text-center`} min={1} placeholder="Other" value={otherPartyVal}
                    onChange={e => { setOtherPartyVal(e.target.value); setPartySize(Number(e.target.value)); }} />
              </div>

              <div className="flex justify-end">
                <button onClick={() => setStep(2)} className="bg-orange-500 hover:bg-orange-600 text-white border-none rounded-xl px-6 py-2.5 font-bold text-sm cursor-pointer transition-colors">
                  Next: Details →
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Channel & Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel>Channel</FieldLabel>
                  <select value={channel} onChange={e => setChannel(e.target.value)} className={selectCls}>
                    {[["walk_in","Walk-In"],["email","Email"],["gemio_app","Gemio App"],["phone","Phone"],["other","Other"]].map(([v, l]) =>
                      <option key={v} value={v}>{l}</option>
                    )}
                  </select>
                </div>
                <div>
                  <FieldLabel>Status</FieldLabel>
                  <select value={status} onChange={e => setStatus(e.target.value)} className={selectCls}>
                    {[["confirmed","Confirmed"],["pending","Pending"]].map(([v, l]) =>
                      <option key={v} value={v}>{l}</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Notes */}
              <div>
                <FieldLabel>Private Booking Comment</FieldLabel>
                <p className="m-0 mb-2 text-[11px] text-gray-400">Not visible to the patron. Add an internal note for your team.</p>
                <textarea value={privateNote} onChange={e => setPrivateNote(e.target.value)} rows={2}
                  placeholder="Internal team note..." className={`${inputCls} resize-y`} />
              </div>
              <div>
                <FieldLabel>Booking Comment</FieldLabel>
                <p className="m-0 mb-2 text-[11px] text-gray-400">Visible to the patron. Guest request or comment (e.g. window seat, allergy)...</p>
                <textarea value={bookingComment} onChange={e => setBookingComment(e.target.value)} rows={2}
                  placeholder="Guest request or special note..." className={`${inputCls} resize-y`} />
              </div>

              {/* Patron Details */}
              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                <FieldLabel>Patron Details</FieldLabel>
                <div className="flex flex-col gap-3 mt-2">
                  <input placeholder="Full Name" value={patron.name} onChange={e => setPatron(p => ({ ...p, name: e.target.value }))} className={inputCls} />
                  <div className="grid grid-cols-2 gap-3">
                    <input placeholder="Phone" value={patron.phone} onChange={e => setPatron(p => ({ ...p, phone: e.target.value }))} className={inputCls} />
                    <input placeholder="Email" value={patron.email} onChange={e => setPatron(p => ({ ...p, email: e.target.value }))} className={inputCls} />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 m-0">Dietary Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {TAG_OPTIONS.map(t => (
                        <button key={t} onClick={() => toggleTag(t)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border cursor-pointer transition-all
                            ${patron.tags.includes(t) ? "border-orange-500 bg-orange-50 text-orange-500" : "border-gray-200 bg-white text-gray-500 hover:border-orange-300"}`}>
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <div onClick={() => setPatron(p => ({ ...p, vip: !p.vip }))}
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center cursor-pointer transition-all flex-shrink-0
                        ${patron.vip ? "border-orange-500 bg-orange-500" : "border-gray-300 bg-white"}`}>
                      {patron.vip && <span className="text-white text-xs font-bold leading-none">✓</span>}
                    </div>
                    <span className="text-sm font-semibold text-stone-700">VIP Guest</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-between gap-3">
                <button onClick={() => setStep(1)} className="bg-gray-100 hover:bg-gray-200 text-stone-700 border-none rounded-xl px-6 py-2.5 font-bold text-sm cursor-pointer transition-colors">
                  ← Back
                </button>
                <button onClick={handleCreateBooking} disabled={submitting} className="bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white border-none rounded-xl px-6 py-2.5 font-bold text-sm cursor-pointer transition-colors">
                  {submitting ? "Creating..." : "Create Booking"}
                </button>
              </div>
              {submitError && <p className="m-0 text-sm text-red-600">{submitError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function ReservationSystem() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>("dashboard");
  const [selectedReservationId, setSelectedReservationId] = useState<string | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [initialLoading, setInitialLoading] = useState(false);
  const [initialError, setInitialError] = useState<string | null>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const { user, accessToken, signOut } = useAuth();

  const restaurantId = useParams().restaurantId || "";
  const navigate = useNavigate();

  const getrestaurant = async () => {
    try {
      setInitialLoading(true);
      setInitialError(null);
      const data = await getSecureRestaurant(restaurantId, accessToken)
      setRestaurant(data);
    } catch (error) {
      console.error("Error fetching restaurant:", error);
      setInitialError(error instanceof Error ? error.message : "Failed to load restaurant data.");
    } finally {
      setInitialLoading(false);
    }
  }
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;

      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(target)
      ) {
        setShowUserMenu(false);
      }

      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(target)
      ) {
        setSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!restaurantId || !accessToken) return;
    getrestaurant();
  }, [restaurantId, accessToken]);

  const tabs: { id: ActiveTab; label: string; Icon: FC }[] = [
    { id: "dashboard", label: "Dashboard",       Icon: DashboardIcon },
    { id: "bookings",  label: "Bookings",         Icon: BookingsIcon  },
    { id: "tables",    label: "Table Management", Icon: TableIcon },
    { id: "floor-map", label: "Floor Map",       Icon: InfoIcon },
    { id: "help-center", label: "Help Center",       Icon: HelpCenterIcon },
  ];

    const handleTabClick = (id: ActiveTab ) => {
    if (id === "help-center") {
      window.open(`/support`, '_blank');
    } else {
      setActiveTab(id);
    }
  };

  const showBlockingLoader = initialLoading && !restaurant;

  return (
    showBlockingLoader ? (
      <div className="flex items-center justify-center h-screen">
        <p className="text-gray-500 text-sm">Loading restaurant data...</p>
      </div>
    ) : initialError ? (
      <div className="flex items-center justify-center h-screen">
        <p className="text-red-600 text-sm">{initialError}</p>
      </div>
    ) : (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=Outfit:wght@400;500;600;700;800&display=swap');
        * { font-family: 'Outfit', sans-serif; box-sizing: border-box; }
        body { margin: 0; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
      `}</style>

      <div className="flex flex-col h-screen overflow-hidden bg-slate-100">
        {/* ── HEADER ─────────────────────────────────────────────────── */}
        <header className="bg-[#FFF8E7] border-b border-amber-100 h-[60px] flex items-center justify-between p-0 px-2 md:px-5 flex-shrink-0 z-20 shadow-sm">
          {/* Left */}
          <div className="flex items-center gap-2 md:gap-4">
            <button onClick={() => setSidebarOpen(o => !o)}
              className="bg-transparent border-none cursor-pointer text-gray-500 hover:text-gray-700 flex items-center p-0 md:p-1.5 rounded-lg hover:bg-amber-100 transition-colors">
              <MenuIcon className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-1 transition-all duration-300 cursor-pointer">
                <LogoIcon />
                <div className="leading-none">
                    <a href="/" className="Logo-sans font-semibold text-2xl text-[#ee5219] block">Gemio</a>
                    <p className="text-xs pl-0.5 -mt-2.5 text-stone-700">reservations</p>
                </div>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-1 md:gap-2.5">
            <div className="hidden lg:inline">
              <OnlineReservationsToggle restaurantId={restaurantId} initialValue={restaurant?.allowOnlineReservations || false} />
            </div>
            <button onClick={() => setActiveTab("bookings")}
                className="bg-stone-800 hover:bg-stone-700 text-white border-none rounded-xl px-3 lg:px-4 py-2 flex items-center gap-2 text-xs font-bold cursor-pointer transition-colors"
                aria-label="See Bookings">
                <Calendar size={15}/>
                <span className="hidden lg:inline">See Bookings</span>
            </button>

            <button onClick={() => setShowBookingModal(true)}
                className="bg-amber-700 hover:bg-amber-600 text-white border-none rounded-xl px-3 lg:px-5 py-2 flex items-center gap-2 text-sm font-bold cursor-pointer transition-colors"
                aria-label="Booking+">
                <Plus size={15} />
                <span className="hidden lg:inline">Booking+</span>
            </button>

            {/* User dropdown */}
            <div ref={userMenuRef} className="relative">
              <button onClick={() => setShowUserMenu(v => !v)}
                className="flex items-center gap-1 md:gap-2 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-xl px-2 md:px-4 py-2 cursor-pointer transition-colors">
                <div className="w-7 h-7 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-[11px] font-bold">{user?.name?.split(" ")[0]}</span>
                </div>
                <span className="hidden md:inline text-sm font-semibold text-stone-900">{user?.name?.split(" ")[0]}</span>
                <ChevronDownIcon />
              </button>

              {showUserMenu && (
                <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-gray-100 rounded-2xl shadow-2xl min-w-[200px] overflow-hidden z-[200]">
                  <button className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-sm font-medium text-stone-700 hover:bg-gray-50 transition-colors text-left" onClick={() => navigate("/dashboard")}>
                    <RestaurantIcon /><span>Back to Manage Restaurant</span>
                  </button>
                  <div className="h-px bg-gray-100" />
                  <button className="flex items-center gap-3 w-full px-4 py-3 bg-transparent border-none cursor-pointer text-sm font-medium text-red-500 hover:bg-red-50 transition-colors text-left" onClick={signOut}>
                    <LogoutIcon /><span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="flex flex-1 overflow-hidden relative">
        {/* ── SIDEBAR ──────────────────────────────────────────────── */}
          <aside
            ref={sidebarRef}
            className={`absolute top-4 left-4 z-41 bg-stone-800 rounded-2xl overflow-hidden shadow-xl transform transition-all duration-200 ease-out ${
                sidebarOpen
                    ? "translate-y-0 scale-100 opacity-100"
                    : "-translate-y-2 scale-95 opacity-0 pointer-events-none"
            }`}>
            <nav className="py-2">
              {tabs.map(({ id, label, Icon }) => (
                <button key={id} onClick={() => handleTabClick(id)}
                  className={`flex items-center w-full border-none cursor-pointer transition-all text-sm px-4 py-3 gap-3 justify-start
                    ${activeTab === id
                      ? "bg-orange-500 text-white font-bold border-l-4 border-orange-300"
                      : "bg-transparent text-stone-400 hover:text-white hover:bg-stone-700 border-l-4 border-transparent font-medium"}`}>
                  <span className="flex-shrink-0 flex"><Icon /></span>
                  <span className="whitespace-nowrap overflow-hidden">{label}</span>
                </button>
              ))}
            </nav>
          </aside>

          {/* ── MAIN CONTENT ─────────────────────────────────────────── */}
          <main className="flex-1 overflow-y-auto bg-slate-100">
            {activeTab === "dashboard" && <DashboardTab onAddBooking={() => setShowBookingModal(true)} STATUS_CONFIG={STATUS_CONFIG} setActiveTab={(tab) => setActiveTab("bookings")} />}
            {activeTab === "bookings"  && <BookingsTab STATUS_CONFIG={STATUS_CONFIG} setActiveTab={(tab, resId) => { setActiveTab(tab as ActiveTab); if (resId) setSelectedReservationId(resId); }}/>}
            {activeTab === "tables"   && <TableManagementTab />}
            {activeTab === "reservation-details" && <ReservationDetail restaurantId={restaurantId} reservationId={selectedReservationId || undefined} onBack={() => setActiveTab("bookings")} />}
            {activeTab === "floor-map" && <FloorPlanBuilder />}
          </main>
        </div>
      </div>

      {showBookingModal && (
        <BookingModal
          onClose={() => setShowBookingModal(false)}
          restaurantName={restaurant?.name}
          restaurantId={restaurantId}
          accessToken={accessToken}
        />
      )}
    </>
  ));
}
